<!-- Zaya Home (design: ims-home-page "Zaya Home"). White full-bleed workspace: hero greeting,
     a collapsible Overview (hidden by default), and a work row with Recently opened (last 2
     projects) + Last opened image. Same data/features as before — projects are limited to the
     ones the user is assigned to, and the annotation counts are the user's own. -->
<template>
<div class="content-wrapper zhome">
  <b-loading :is-full-page="false" :active.sync="loading" />

  <div v-if="!loading" class="zh">

    <!-- ===================== HERO ===================== -->
    <section class="zh-hero">
      <h1 class="zh-title">{{ $t('welcome-back', {name: firstName}) }}</h1>
      <p class="zh-sub">{{ today }} — {{ $t('workspace-at-a-glance') }}</p>
    </section>

    <!-- ===================== 01 · OVERVIEW (collapsible, hidden by default) ===================== -->
    <section class="zh-section">
      <div class="zh-sec-head">
        <div class="zh-sec-title">
          <span class="zh-num">01</span>
          <h2>{{ $t('overview') }}</h2>
        </div>
        <button class="zh-toggle" @click="overviewOpen = !overviewOpen">
          {{ overviewOpen ? $t('hide') : $t('show') }}
          <span class="zh-chev" :class="{open: overviewOpen}">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748B" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
          </span>
        </button>
      </div>

      <div class="zh-card zh-overview">
        <div v-show="overviewOpen" class="zh-kpis">
          <!-- Projects -->
          <div class="zh-kpi">
            <div class="zh-kpi-top">
              <span class="zh-kpi-ico blue"><i class="fas fa-layer-group"></i></span>
              <span class="zh-kpi-code">P-01</span>
            </div>
            <div class="zh-kpi-num">{{ projects ? projects.length : '—' }}</div>
            <div class="zh-kpi-lbl">{{ $t('projects') }}</div>
            <div class="zh-kpi-cap">{{ $t('active-in-your-workspace') }}</div>
            <router-link class="zh-kpi-link" to="/projects">
              {{ $t('view-all-projects') }}
              <span class="zh-kpi-arrow"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="18" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg></span>
            </router-link>
          </div>

          <!-- Images -->
          <div class="zh-kpi">
            <div class="zh-kpi-top">
              <span class="zh-kpi-ico violet"><i class="fas fa-image"></i></span>
              <span class="zh-kpi-code">I-02</span>
            </div>
            <div class="zh-kpi-num">{{ nbImages != null ? nbImages : '—' }}</div>
            <div class="zh-kpi-lbl">{{ $t('images') }}</div>
            <div class="zh-kpi-cap">{{ $t('slides-across-projects') }}</div>
          </div>

          <!-- Manual annotations -->
          <div class="zh-kpi">
            <div class="zh-kpi-top">
              <span class="zh-kpi-ico teal"><i class="fas fa-draw-polygon"></i></span>
              <span class="zh-kpi-code">A-03</span>
            </div>
            <div class="zh-kpi-num">{{ nbUserAnnots != null ? nbUserAnnots : '—' }}</div>
            <div class="zh-kpi-lbl">{{ $t('manual-annotations') }}</div>
            <div class="zh-kpi-cap">{{ $t('drawn-across-images') }}</div>
          </div>

          <!-- Reviewed annotations -->
          <div class="zh-kpi">
            <div class="zh-kpi-top">
              <span class="zh-kpi-ico green"><i class="fas fa-check-circle"></i></span>
              <span class="zh-kpi-code">R-04</span>
            </div>
            <div class="zh-kpi-num">{{ nbReviewed != null ? nbReviewed : '—' }}</div>
            <div class="zh-kpi-lbl">{{ $t('reviewed-annotations') }}</div>
            <div class="zh-kpi-bar"><div class="zh-kpi-fill" :style="{width: reviewWidth}"></div></div>
            <div class="zh-kpi-cap mono">{{ reviewedCaption }}</div>
          </div>
        </div>

        <!-- collapsed summary -->
        <div v-if="!overviewOpen" class="zh-overview-sum">
          <span class="n">{{ projects ? projects.length : 0 }}</span><span class="l">{{ $t('projects') }}</span>
          <span class="dot"></span>
          <span class="n">{{ nbImages != null ? nbImages : 0 }}</span><span class="l">{{ $t('images') }}</span>
          <span class="dot"></span>
          <span class="n">{{ nbUserAnnots != null ? nbUserAnnots : 0 }}</span><span class="l">{{ $t('manual-annotations') }}</span>
          <span class="dot"></span>
          <span class="n">{{ nbReviewed != null ? nbReviewed : 0 }}</span><span class="l">{{ $t('reviewed-annotations') }}</span>
        </div>
      </div>
    </section>

    <!-- ===================== WORK ROW ===================== -->
    <section class="zh-work">

      <!-- 02 · Recently opened (last 2 projects) -->
      <div class="zh-col">
        <div class="zh-sec-head">
          <div class="zh-sec-title">
            <span class="zh-num">02</span>
            <h2>{{ $t('recently-opened') }}</h2>
            <span class="zh-badge">{{ $t('last-2-projects') }}</span>
          </div>
          <span class="zh-sec-note">{{ $t('updated-just-now') }}</span>
        </div>

        <div class="zh-card zh-recent">
          <b-message v-if="!recentProjects" type="is-danger" has-icon icon-size="is-small">
            {{ $t('failed-fetch-recent-projects') }}
          </b-message>
          <template v-else-if="recentProjects.length">
            <div class="zh-rproj" v-for="(project, idx) in recentProjects" :key="project.id">
              <hr v-if="idx > 0" class="zh-rsep">
              <div class="zh-rproj-head">
                <div class="zh-rproj-id">
                  <span class="zh-rproj-ico"><i class="fas fa-layer-group"></i></span>
                  <div>
                    <router-link class="zh-rproj-name" :to="`/project/${project.id}`">{{ project.name }}</router-link>
                    <div class="zh-rproj-meta">{{ $t('n-images', {count: project.numberOfImages}) }}</div>
                  </div>
                </div>
                <router-link class="zh-viewall" :to="`/project/${project.id}`">
                  {{ $t('view-all') }}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </router-link>
              </div>
              <list-images-preview v-if="project.numberOfImages > 0" :project="project" />
              <div v-else class="zh-rproj-empty">
                <span class="zh-rproj-empty-ico"><i class="far fa-image"></i></span>
                <div>
                  <div class="t">{{ $t('no-image-recently-opened') }}</div>
                  <div class="s">{{ $t('upload-or-open-slide-hint') }}</div>
                </div>
              </div>
            </div>
          </template>
          <div v-else class="zh-empty">{{ $t('no-recent-project') }}</div>
        </div>
      </div>

      <!-- 03 · Last opened image -->
      <div class="zh-col">
        <div class="zh-sec-head">
          <div class="zh-sec-title">
            <span class="zh-num">03</span>
            <h2>{{ $t('last-opened-image') }}</h2>
          </div>
          <span v-if="lastOpenedImage" class="zh-pill-active"><span class="d"></span>{{ $t('active') }}</span>
        </div>

        <div class="zh-card zh-lastimg">
          <image-preview
            v-if="lastOpenedImage"
            :image="lastOpenedImage"
            :fullHeightCard="true"
            :showProject="true"
            :blindMode="lastOpenedImage.blindMode"
          />
          <div v-else class="zh-empty">{{ $t('no-recent-image') }}</div>
        </div>
      </div>
    </section>
  </div>
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';

import ListImagesPreview from '@/components/image/ListImagesPreview';
import ImagePreview from '@/components/image/ImagePreview';

import {ImageInstanceCollection, ProjectCollection} from '@/api';

export default {
  name: 'global-dashboard',
  components: {
    ListImagesPreview,
    ImagePreview
  },
  props: {
    // Recently-opened shows only the last 2 projects.
    nbRecent: {
      type: Number,
      default: 2
    }
  },
  data() {
    return {
      projects: null,
      recentProjectsId: null,
      recentImages: [],
      nbUserAnnots: null,
      nbReviewed: null,
      overviewOpen: false, // Overview hidden by default
      loading: true
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    currentAccount: get('currentUser/account'),
    firstName() {
      return (this.currentAccount && this.currentAccount.firstName)
        || (this.currentUser && (this.currentUser.firstname || this.currentUser.name))
        || this.$t('user');
    },
    today() {
      try {
        return new Date().toLocaleDateString(undefined, {weekday: 'long', month: 'long', day: 'numeric'});
      } catch (e) {
        return '';
      }
    },
    nbImages() {
      if (!this.projects) {
        return null;
      }
      return this.projects.array.reduce((count, project) => count + project.numberOfImages, 0);
    },
    reviewWidth() {
      if (!this.nbUserAnnots || this.nbReviewed == null) {
        return '0%';
      }
      let pct = Math.min(100, Math.round((this.nbReviewed / this.nbUserAnnots) * 100));
      return pct + '%';
    },
    reviewedCaption() {
      return `${this.nbReviewed != null ? this.nbReviewed : 0} / ${this.nbUserAnnots != null ? this.nbUserAnnots : 0} ${this.$t('reviewed').toLowerCase()}`;
    },
    recentProjects() {
      if (!this.recentProjectsId || !this.projects) {
        return null;
      }
      let array = [];
      this.recentProjectsId.forEach(id => {
        let project = this.projects.array.find(project => project.id === id);
        if (project) {
          array.push(project);
        }
      });
      return array;
    },
    lastOpenedImage() {
      if (this.recentImages && this.recentImages.length > 0 && this.projects) {
        let lastOpened = this.recentImages[0];
        let project = this.projects.array.find(project => project.id === lastOpened.project);
        if (project) {
          lastOpened.projectName = project.name;
          lastOpened.blindMode = project.blindMode;
        }
        return lastOpened;
      }
      return false;
    }
  },
  methods: {
    async fetchProjects() {
      // Only the projects the current user is assigned to.
      this.projects = await ProjectCollection.fetchAll({filterKey: 'user', filterValue: this.currentUser.id});
    },
    async fetchNbAnnots() {
      this.nbUserAnnots = await this.currentUser.fetchNbAnnotations();
    },
    async fetchNbReviewedAnnots() {
      this.nbReviewed = await this.currentUser.fetchNbAnnotations(true);
    },
    async fetchRecentProjectsId() {
      let listRecent = await ProjectCollection.fetchLastOpened(this.nbRecent);
      this.recentProjectsId = listRecent.map(recent => recent.id);
    },
    async fetchRecentImages() {
      this.recentImages = await ImageInstanceCollection.fetchLastOpened({max: 1});
    }
  },
  async created() {
    await Promise.all([
      this.fetchProjects(),
      this.fetchNbAnnots(),
      this.fetchNbReviewedAnnots(),
      this.fetchRecentProjectsId(),
      this.fetchRecentImages()
    ].map(p => p.catch(e => console.log(e)))); // ignore errors (handled in template), still resolve all
    this.loading = false;
  }
};
</script>

<style scoped lang="scss">
$ink: #0B1B3B;
$muted: #64748B;
$muted2: #94A3B8;
$line: #ECEFF4;
$blue: #2563EB;

.content-wrapper { padding: 0; }

.zh {
  background: #fff;
  min-height: calc(100vh - 3.5rem);
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 0 44px 44px;
  color: $ink;
  font-family: Manrope, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* hero — override the global h1 (centered/uppercase/padded) so it's left-aligned sentence-case */
.zh-hero { padding: 34px 0 26px; border-bottom: 1px solid #EEF1F6; text-align: left; }
.zh-title {
  margin: 0; padding: 0;
  font-size: 34px; font-weight: 800; letter-spacing: -0.025em; color: $ink; line-height: 1.1;
  text-align: left;
  text-transform: none;
}
.zh-sub { margin: 12px 0 0; padding: 0; font-size: 15px; font-weight: 500; color: $muted; text-align: left; }

/* section heads */
.zh-section { padding: 28px 0 4px; }
.zh-sec-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.zh-sec-title { display: flex; align-items: baseline; gap: 12px; }
.zh-sec-title h2 { margin: 0; font-size: 17px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: $ink; }
.zh-num { font: 600 11.5px ui-monospace, 'JetBrains Mono', monospace; letter-spacing: 0.2em; color: #B4BECE; }
.zh-sec-note { font-size: 12px; font-weight: 500; color: $muted2; }
.zh-badge {
  display: inline-flex; align-items: center; height: 22px; padding: 0 9px; border-radius: 999px;
  background: #EAF1FE; color: $blue; font-size: 11.5px; font-weight: 700;
}

.zh-toggle {
  display: flex; align-items: center; gap: 9px; height: 36px; padding: 0 8px 0 14px;
  border: 1px solid #E4E8EF; border-radius: 10px; background: #fff; color: #475569;
  font-size: 12.5px; font-weight: 700; cursor: pointer; transition: background 0.15s, border-color 0.15s;
  &:hover { background: #F4F6FA; border-color: #D5DDE9; }
  .zh-chev {
    display: flex; align-items: center; justify-content: center; width: 22px; height: 22px;
    border-radius: 7px; background: #F1F4F9; transition: transform 0.25s ease; transform: rotate(-90deg);
  }
  .zh-chev.open { transform: rotate(0deg); }
}

/* cards */
.zh-card { border: 1px solid $line; border-radius: 20px; background: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04); }
.zh-overview { overflow: hidden; }

/* KPI grid */
.zh-kpis { display: grid; grid-template-columns: repeat(4, 1fr); }
.zh-kpi { padding: 26px 28px; border-right: 1px solid #F0F2F6; transition: background 0.15s; }
.zh-kpi:last-child { border-right: 0; }
.zh-kpi:hover { background: #FAFBFE; }
.zh-kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.zh-kpi-ico {
  width: 44px; height: 44px; border-radius: 13px; display: flex; align-items: center; justify-content: center; font-size: 18px;
  &.blue { background: #EAF1FE; color: #2563EB; }
  &.violet { background: #F0ECFE; color: #7C3AED; }
  &.teal { background: #E5F6F1; color: #0D9488; }
  &.green { background: #E4F6EA; color: #16A34A; }
}
.zh-kpi-code { font: 600 10.5px ui-monospace, 'JetBrains Mono', monospace; letter-spacing: 0.12em; color: #C2CBD9; }
.zh-kpi-num { font-size: 42px; font-weight: 800; letter-spacing: -0.03em; color: $ink; line-height: 1; font-variant-numeric: tabular-nums; }
.zh-kpi-lbl { margin-top: 9px; font-size: 13.5px; font-weight: 700; color: #334155; }
.zh-kpi-cap { margin-top: 3px; font-size: 12px; font-weight: 500; color: $muted2; }
.zh-kpi-cap.mono { margin-top: 7px; font: 600 11px ui-monospace, 'JetBrains Mono', monospace; }
.zh-kpi-bar { margin-top: 12px; height: 6px; border-radius: 999px; background: #EEF2F7; overflow: hidden; }
.zh-kpi-fill { height: 100%; background: linear-gradient(90deg, #16A34A, #22C55E); border-radius: 999px; transition: width 0.8s ease; }
.zh-kpi-link {
  margin-top: 18px; display: inline-flex; align-items: center; gap: 8px; height: 36px; padding: 0 8px 0 14px;
  border: 1px solid #DCE7FB; border-radius: 10px; background: #F4F8FF; color: #1D4ED8;
  font-size: 12.5px; font-weight: 700; text-decoration: none; transition: background 0.15s, border-color 0.15s;
  &:hover { background: #EAF1FE; border-color: #BFD4FB; }
  .zh-kpi-arrow { display: flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 7px; background: #E1ECFE; }
}

/* collapsed overview summary */
.zh-overview-sum {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 18px 28px;
  .n { font-size: 18px; font-weight: 800; color: $ink; }
  .l { font-size: 13px; font-weight: 600; color: $muted; }
  .dot { width: 4px; height: 4px; border-radius: 999px; background: #D5DDE9; }
}

/* work row — fills the remaining viewport height so the cards are tall */
.zh-work { flex: 1; min-height: 0; padding: 28px 0 0; display: grid; grid-template-columns: 1.62fr 1fr; gap: 24px; align-items: stretch; }
.zh-col { display: flex; flex-direction: column; min-height: 0; }
.zh-col .zh-card { flex: 1; }

/* hide the inner "View all" tile that ListImagesPreview renders next to the thumbnails —
   the section already has a "View all" in the project header. */
.zh-recent ::v-deep .columns .column.is-narrow { display: none; }

/* last-opened image fills its tall card (image hero) */
.zh-lastimg { display: flex; }
.zh-lastimg ::v-deep .card {
  height: 100%; width: 100%; display: flex; flex-direction: column;
  box-shadow: none; border: 0; background: transparent;
}
.zh-lastimg ::v-deep .recent-image { flex: 1; display: block; min-height: 340px; }
.zh-lastimg ::v-deep .recent-image .image {
  height: 100%; padding-top: 0; border-radius: 14px; border: 1px solid $line;
}
.zh-lastimg ::v-deep .card-content { padding: 16px 6px 4px; }

.zh-recent { padding: 22px 24px; }
.zh-rsep { border: 0; border-top: 1px solid #F0F2F6; margin: 22px 0; }
.zh-rproj-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.zh-rproj-id { display: flex; align-items: center; gap: 12px; }
.zh-rproj-ico {
  width: 40px; height: 40px; border-radius: 12px; flex-shrink: 0;
  background: linear-gradient(135deg, #EAF1FE, #DCE7FB); color: #2563EB;
  display: flex; align-items: center; justify-content: center;
}
.zh-rproj-name { font-size: 15px; font-weight: 700; color: #1E293B; text-decoration: none; &:hover { color: $blue; } }
.zh-rproj-meta { font-size: 12px; font-weight: 500; color: $muted2; margin-top: 2px; }
.zh-viewall {
  display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 14px;
  border: 1px solid #E4E8EF; border-radius: 10px; background: #fff; color: $blue;
  font-size: 13px; font-weight: 700; text-decoration: none; transition: background 0.15s, border-color 0.15s;
  &:hover { background: #F4F8FF; border-color: #BFD4FB; }
}
.zh-rproj-empty {
  display: flex; align-items: center; gap: 16px; border: 1px dashed #E4E8EF; border-radius: 16px;
  padding: 20px 22px; background: #FAFBFD;
  .zh-rproj-empty-ico {
    width: 46px; height: 46px; border-radius: 12px; background: #fff; border: 1px solid $line;
    display: flex; align-items: center; justify-content: center; color: $muted2; flex-shrink: 0;
  }
  .t { font-size: 14px; font-weight: 600; color: #334155; }
  .s { font-size: 12.5px; font-weight: 500; color: $muted2; margin-top: 2px; }
}

.zh-lastimg { padding: 18px; }

.zh-pill-active {
  display: inline-flex; align-items: center; gap: 6px; height: 24px; padding: 0 10px; border-radius: 999px;
  background: #E4F6EA; color: #16A34A; font-size: 12px; font-weight: 700;
  .d { width: 6px; height: 6px; border-radius: 999px; background: #16A34A; }
}

.zh-empty { color: $muted2; padding: 40px 0; text-align: center; font-size: 13.5px; font-weight: 500; }

@media (max-width: 1100px) {
  .zh { padding: 0 20px 32px; }
  .zh-kpis { grid-template-columns: repeat(2, 1fr); }
  .zh-kpi:nth-child(2) { border-right: 0; }
  .zh-kpi:nth-child(1), .zh-kpi:nth-child(2) { border-bottom: 1px solid #F0F2F6; }
  .zh-work { grid-template-columns: 1fr; }
}
</style>
